import doNothing from '@opsbase/lib-general/utils/doNothing';
import DefaultGradeKeys from '@opsbase/lib-grading/DefaultGradeKeys';
import DefaultGradingCalculatorKey from '@opsbase/lib-grading/DefaultGradingCalculatorKey';
import Grade from '@opsbase/lib-grading/Grade';
import GradingRuleType from '@opsbase/lib-grading/GradingRuleType';
import i18n from '@opsbase/lib-i18n';
import Palette from '@opsbase/lib-palette';
import useSelf from '@opsbase/react-ctrl-self/useSelf';
import useSelfActions from '@opsbase/react-ctrl-self/useSelfActions';
import useOrgFindById from '@opsbase/react-gql-hooks/organisation/useOrgFindById';
import useOrgUpdate from '@opsbase/react-gql-hooks/organisation/useOrgUpdate';
import useCallforward from '@opsbase/react-hooks/useCallforward';
import markdown from '@opsbase/react-markdown';
import AppPage from '@opsbase/web-app/AppPage';
import useCss from '@opsbase/web-css';
import EditableList from '@opsbase/web-editablelist';
import AddIcon from '@opsbase/web-icons/icons/AddIcon';
import DeleteIcon from '@opsbase/web-icons/icons/DeleteIcon';
import EditIcon from '@opsbase/web-icons/icons/EditIcon';
import Divider from '@opsbase/web-layout/Divider';
import Horizontal from '@opsbase/web-layout/Horizontal';
import Pane from '@opsbase/web-layout/Pane';
import Vertical from '@opsbase/web-layout/Vertical';
import useNotificationActions from '@opsbase/web-notification/useNotificationActions';
import GradeColorPicker from '@opsbase/web-pickers/GradeColorPicker';
import PassStatusPicker from '@opsbase/web-pickers/PassStatusPicker';
import Button from '@opsbase/web-ui/Button';
import IconButton from '@opsbase/web-ui/IconButton';
import Text from '@opsbase/web-ui/Text';
import React, { useCallback, useState } from 'react';
import * as uuid from 'uuid';
import { GradingCalculatorDialog } from '../dialogs/GradingCalculator/GradingCalculatorDialog';
const makeMyClasses = () => ({
    gradingCalculator: {
        margin: '8px 0 4px 0',
        padding: 12,
        backgroundColor: 'white',
        border: `1px solid ${Palette.InputBorderGrey.main}`,
        borderRadius: 4,
    },
    addGradingCalculator: {
        padding: 10,
        marginTop: 8,
        borderRadius: 4,
        '& .MuiButton-label': {
            justifyContent: 'left !important',
        },
    },
});
const canEditGrade = (item) => {
    return item.key !== DefaultGradeKeys.UNDETERMINED;
};
export function AdvancedOpGradesPage() {
    var _a, _b;
    const self = useSelf();
    const { updateSettings } = useSelfActions();
    const { data: org } = useOrgFindById({ variables: { id: self.organisationId } });
    const updateOrg = useOrgUpdate();
    const { raiseError } = useNotificationActions();
    const [editGradingCalculator, setEditGradingCalculator] = useState();
    const grades = (_a = org === null || org === void 0 ? void 0 : org.settings) === null || _a === void 0 ? void 0 : _a.grades;
    const gradingCalculators = (_b = org === null || org === void 0 ? void 0 : org.settings) === null || _b === void 0 ? void 0 : _b.gradingCalculators;
    const myCss = useCss(makeMyClasses);
    const handleChange = useCallback(patch => {
        updateOrg({
            id: org.id,
            patch: {
                settings: {
                    ...patch,
                },
            },
        }, {
            optimisticResponse: {
                __typename: 'Mutation',
                updateOrg: {
                    __typename: 'Organisation',
                    ...org,
                    ...patch,
                },
            },
        })
            .then(updatedOrg => {
            updateSettings(updatedOrg.settings);
        })
            .catch(doNothing);
    }, [org, updateSettings, updateOrg]);
    const handleChangeColor = useCallforward(grade => color => {
        grade.color = color;
        handleChange({ grades: grades });
    }, [handleChange, grades]);
    const handleChangePassFailStatus = useCallforward(grade => passStatus => {
        grade.passStatus = passStatus;
        handleChange({ grades: grades });
    }, [handleChange, grades]);
    const handleAddGrades = useCallback((newItems, idx, listId, deleteCount) => {
        const newGrades = newItems.map(({ key, title }) => new Grade(key, title));
        grades.splice(idx, deleteCount, ...newGrades);
        handleChange({ grades });
    }, [grades, handleChange]);
    const handleDeleteGrade = useCallback((grade) => {
        let isGradeUsed = false;
        gradingCalculators.forEach(c => {
            if (c.failoverGrade === grade.key || c.immediateFailGrade === grade.key) {
                isGradeUsed = true;
            }
            c.rules.forEach(r => {
                if (r.grade === grade.key) {
                    isGradeUsed = true;
                }
            });
        });
        if (isGradeUsed) {
            raiseError(i18n.t('cannot-remove-grade'));
            return;
        }
        const idx = grades.findIndex(g => g.key === grade.key);
        grades.splice(idx, 1);
        handleChange({ grades });
    }, [grades, gradingCalculators, handleChange, raiseError]);
    const handleChangeGrades = useCallback(v => handleChange({ grades: v }), [handleChange]);
    const handleAfterGrade = useCallback((grade) => (React.createElement(Horizontal, { gutter: true, justify: "flex-end" },
        React.createElement(GradeColorPicker, { noBorder: true, noMargin: true, disabled: grade.key === DefaultGradeKeys.UNDETERMINED, value: grade.color, onChange: handleChangeColor(grade) }),
        React.createElement(PassStatusPicker, { noMargin: true, noBorder: true, disabled: grade.key === DefaultGradeKeys.UNDETERMINED, value: grade.passStatus, onChange: handleChangePassFailStatus(grade) }))), [handleChangeColor, handleChangePassFailStatus]);
    const handleEditGradingCalculator = useCallback((gradingCalculator) => () => {
        setEditGradingCalculator(gradingCalculator);
    }, []);
    const handleCloseGradingCalculatorDialog = useCallback(() => {
        setEditGradingCalculator(undefined);
    }, []);
    const handleChangeGradingCalculator = useCallback(v => {
        if (v.new) {
            delete v.new;
            gradingCalculators === null || gradingCalculators === void 0 ? void 0 : gradingCalculators.push(v);
        }
        handleChange({
            gradingCalculators: gradingCalculators === null || gradingCalculators === void 0 ? void 0 : gradingCalculators.map(g => (g.key === v.key ? v : g)),
        });
        handleCloseGradingCalculatorDialog();
    }, [handleChange, handleCloseGradingCalculatorDialog, gradingCalculators]);
    const handleAddGradingCalculator = useCallback(() => {
        setEditGradingCalculator({
            new: true,
            key: uuid.v4(),
            title: 'New grading calculator',
            rules: [
                {
                    type: GradingRuleType.SCOREGTE,
                    scoreGte: 50,
                    scoreLt: 0,
                    grade: DefaultGradeKeys.UNDETERMINED,
                },
            ],
            immediateFailGrade: DefaultGradeKeys.UNDETERMINED,
            failoverGrade: DefaultGradeKeys.UNDETERMINED,
        });
    }, []);
    const handleDeleteGradingCalculator = useCallforward(v => () => {
        handleChange({
            gradingCalculators: gradingCalculators.filter(g => g.key !== v.key),
        });
    }, [handleChange, gradingCalculators]);
    return (React.createElement(AppPage, null,
        React.createElement(Vertical, { padding: true, style: { overflow: 'auto' } },
            React.createElement(Horizontal, { padding: true },
                React.createElement(Pane, { grow: true },
                    React.createElement(Text, { variant: "h2" }, markdown(i18n.t('grades-question'))),
                    (grades === null || grades === void 0 ? void 0 : grades.length) ? (React.createElement(EditableList, { autoSelectIdx: 0, placeholder: i18n.t('enter-name-of-new-grade'), items: grades, idField: "key", valueField: "title", canAddInline: true, canAddAtBottom: true, canDelete: canEditGrade, afterItem: handleAfterGrade, onAdd: handleAddGrades, onChange: handleChangeGrades, onDelete: handleDeleteGrade })) : ('')),
                React.createElement(Pane, { padding: true },
                    React.createElement(Divider, { vertical: true })),
                React.createElement(Pane, { grow: true },
                    React.createElement(Text, { variant: "h2" }, markdown(i18n.t('op-gradingcalculators-question'))), gradingCalculators === null || gradingCalculators === void 0 ? void 0 :
                    gradingCalculators.map(gradingCalculator => (React.createElement(Horizontal, { centered: true, gutter: true, key: gradingCalculator.key, classes: { root: myCss.gradingCalculator } },
                        React.createElement(Pane, { grow: true, onClick: handleEditGradingCalculator(gradingCalculator) },
                            React.createElement(Horizontal, { justify: "flex-start", centered: true, gutter: true },
                                React.createElement(Text, null, gradingCalculator.title),
                                React.createElement(EditIcon, { color: Palette.LavendarGrey, size: 16 }))),
                        gradingCalculator.key !== DefaultGradingCalculatorKey ? (React.createElement(IconButton, { icon: React.createElement(DeleteIcon, { color: Palette.GreyBlue }), onClick: handleDeleteGradingCalculator(gradingCalculator) })) : (React.createElement("div", { style: { width: 42 } }))))),
                    React.createElement(Button, { classes: { root: myCss.addGradingCalculator }, fullWidth: true, background: true, label: i18n.t('add-grading-method'), icon: React.createElement(AddIcon, null), onClick: handleAddGradingCalculator })),
                !!editGradingCalculator && (React.createElement(GradingCalculatorDialog, { grades: grades, value: editGradingCalculator, onChange: handleChangeGradingCalculator, onCancel: handleCloseGradingCalculatorDialog }))))));
}
export default AdvancedOpGradesPage;
